import { useQuery } from "@tanstack/react-query";

import { useCallback, useMemo, useRef, useState } from "react";

import {
  usePatientClaim,
  useResetPatientClaim,
} from "@/hooks/patient-claim/use-patient-claim";

import { toast } from "@/lib/use-toast";

import { PatientStatus } from "@/types/enums/user-type";
import { PublicEventTiny } from "@/types/event";
import { SelfSchedulePatient } from "@/types/self-schedule";

import { tenantQueryOptions } from "@/query/tenant";
import { authService } from "@/services/auth-service";

interface UsePatientVerificationReturn {
  // State
  isSigningIn: boolean;
  setIsSigningIn: (value: boolean) => void;

  // True while we don't yet know if there's a signed-in patient — Firebase
  // auth hasn't replayed its persisted session, or it has and we're still
  // fetching the patient row. Callers gate page rendering on this so the
  // unauthenticated start doesn't flash before the signed-in view.
  isResolvingSession: boolean;

  // Patient data
  patient: SelfSchedulePatient | null;
  allFutureAppointments: PublicEventTiny[];
  blockingAppointment?: PublicEventTiny;
  isBlocking: (appointment: PublicEventTiny) => boolean;

  // Handlers
  handleExistingPatientServiceClick: (
    serviceId: string,
    onServiceSelect: (serviceId: string) => void,
  ) => void;
  handlePatientVerifiedWithService: (
    patient: SelfSchedulePatient,
    serviceId: string,
    onServiceSelect: (serviceId: string) => void,
  ) => void;
  handlePatientVerifiedForService: (
    patient: SelfSchedulePatient,
    onServiceSelect: (serviceId: string) => void,
  ) => void;

  signOutPatient: () => void;
}

/**
 * Hook that encapsulates the patient verification flow for self-schedule.
 * Handles:
 * - Patient verification via MFA
 * - Checking for appointments within 24 hours
 * - Visibility filtering based on patient status
 * - Blacklist checking
 * - Service visibility validation
 */
export const usePatientVerification = (): UsePatientVerificationReturn => {
  const [isSigningIn, setIsSigningIn] = useState<boolean>(false);
  const [pendingServiceId, setPendingServiceId] = useState<string | null>(null);
  const pendingServiceSelectRef = useRef<((serviceId: string) => void) | null>(
    null,
  );

  const { claimedPatient: patient, isResolving: isResolvingSession } =
    usePatientClaim();
  const resetPatientClaim = useResetPatientClaim();

  const {
    data,
    isLoading: isLoadingAllowMultipleBookingsForDifferentAppointments,
  } = useQuery({
    ...tenantQueryOptions.public(),
    select: (data) => ({
      allowMultipleBookingsForDifferentAppointments:
        data?.allowMultipleBookingsForDifferentAppointments,
    }),
  });

  const { allowMultipleBookingsForDifferentAppointments } = useMemo(
    () =>
      data || {
        allowMultipleBookingsForDifferentAppointments: null,
      },
    [data],
  );

  // Check if an appointment is within 24 hours (blocking cancel/reschedule)
  const isBlocking = useCallback(
    (appointment: PublicEventTiny) => {
      if (allowMultipleBookingsForDifferentAppointments || !patient?.id) {
        return false;
      }
      const appointmentTime = new Date(appointment.start).getTime();
      const now = Date.now();
      const hoursUntil = (appointmentTime - now) / (1000 * 60 * 60);
      return hoursUntil <= 24;
    },
    [allowMultipleBookingsForDifferentAppointments, patient?.id],
  );

  const futureAppointment = useMemo(() => {
    const appointments = patient?.futureAppointments ?? [];
    if (appointments.length === 0) return undefined;
    return appointments[0];
  }, [patient?.futureAppointments]);

  // Derive blocking appointment from existing appointment
  const blockingAppointment = useMemo(() => {
    if (
      !futureAppointment ||
      isLoadingAllowMultipleBookingsForDifferentAppointments
    ) {
      return undefined;
    }
    return isBlocking(futureAppointment) ? futureAppointment : undefined;
  }, [
    futureAppointment,
    isLoadingAllowMultipleBookingsForDifferentAppointments,
    isBlocking,
  ]);

  /**
   * Handle click on service that requires existing patient verification
   */
  const handleExistingPatientServiceClick = (
    serviceId: string,
    onServiceSelect: (serviceId: string) => void,
  ) => {
    // Prevent service selection if patient has a blocking appointment
    if (blockingAppointment) {
      return;
    }

    if (!patient) {
      pendingServiceSelectRef.current = onServiceSelect;
      setPendingServiceId(serviceId);
      setIsSigningIn(true);
    } else {
      // If patient is already verified, proceed with service selection
      handlePatientVerifiedWithService(patient, serviceId, onServiceSelect);
    }
  };

  /**
   * Handle patient verification completion with service selection
   */
  const handlePatientVerifiedWithService = (
    verifiedPatient: SelfSchedulePatient,
    serviceId: string,
    onServiceSelect: (serviceId: string) => void,
  ) => {
    if (!verifiedPatient) return;

    setIsSigningIn(false);
    setPendingServiceId(null);

    // Check if patient has appointments for existing patient services
    if (verifiedPatient.patientStatus !== PatientStatus.EXISTING) {
      toast({
        title: "Returning Patients Only",
        description:
          "Sorry, you must be a returning patient to book this service.",
        variant: "error",
      });
      return;
    }

    onServiceSelect(serviceId);
  };

  /**
   * Handle patient verification completion (for services)
   */
  const handlePatientVerifiedForService = (
    verifiedPatient: SelfSchedulePatient,
    onServiceSelect: (serviceId: string) => void,
  ) => {
    if (!verifiedPatient || !pendingServiceId) return;

    setIsSigningIn(false);
    const serviceId = pendingServiceId;
    const pendingServiceSelect = pendingServiceSelectRef.current;
    pendingServiceSelectRef.current = null;
    setPendingServiceId(null);

    // Check if patient has appointments for existing patient services
    if (verifiedPatient.patientStatus !== PatientStatus.EXISTING) {
      toast({
        title: "Returning Patients Only",
        description:
          "Sorry, you must be a returning patient to book this service.",
        variant: "error",
      });
      return;
    }

    (pendingServiceSelect ?? onServiceSelect)(serviceId);
  };

  const signOutPatient = () => {
    // Sign out of Firebase so the auth-state listener clears the patient-
    // claim query.
    void authService.logout(true);
    resetPatientClaim();
    setIsSigningIn(false);
    pendingServiceSelectRef.current = null;
    setPendingServiceId(null);
  };

  return {
    // State
    isSigningIn,
    setIsSigningIn,
    isResolvingSession,

    // Patient data
    patient,

    // Handlers
    handleExistingPatientServiceClick,
    handlePatientVerifiedWithService,
    handlePatientVerifiedForService,

    // Appointments
    allFutureAppointments: patient?.futureAppointments ?? [],
    blockingAppointment,
    isBlocking,

    // sign out
    signOutPatient,
  };
};
